import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import FormHelperText from '@mui/material/FormHelperText'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import SingleSelect from '../../components/SingleSelect'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import ErrorBanner from '../../components/shared/ErrorBanner'
import PillToggle from '../../components/shared/PillToggle'
import {
    CATEGORIES,
    CTA_EXTERNAL,
    CTA_INTERNAL,
    DRAFT,
    INTERNAL_ROUTES,
    MAX_MESSAGE,
    MAX_TITLE,
    PUBLISHED,
    TAGS,
    audienceCountBody,
    audienceSummary,
    audienceTypeOf,
    emptyTrainerNotificationForm,
    formAudience,
    formFromNotification,
    trainerNotificationPayload,
    validateTrainerNotification,
    AUDIENCE,
} from '../../helpers/trainerNotifications'
import TrainerNotificationService from '../../services/trainerNotificationService'
import { errorMessage } from '../../services/axios/errorMessage'
import { isNoAnswer } from '../../services/axios/withApiError'
import {
    ICON,
    RADIUS,
    SURFACE,
    TAG,
    TEXT,
    cardSx,
    pageSx,
} from '../../theme/tokens'
import AudienceSegments from './AudienceSegments'
import StripPreview from './StripPreview'
import { NotificationHeader, TAG_ICON } from './notificationShared'

// The audience count is asked this long after the audience stops changing.
const COUNT_DELAY_MS = 500

// Form fields whose change also clears the audience error.
const AUDIENCE_FIELDS = [
    'audienceMode',
    'allTrainers',
    'segments',
    'gradeGroups',
    'regions',
    'productIds',
    'trainerIds',
]

const fullRow = { gridColumn: '1 / -1' }

/** "12/120" under a field with a limit, beside its error when it has one. */
const Counter = ({ error, length, max }) => (
    <Box
        component="span"
        sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
    >
        <span>{error ?? ''}</span>
        <span>
            {length}/{max}
        </span>
    </Box>
)
Counter.propTypes = {
    error: PropTypes.string,
    length: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
}

/**
 * Create a trainer dashboard notification, or edit a draft when the route
 * names one (`/trainer-notifications/:notificationId/edit`). It has a fixed
 * layout (the strip and Updates card), so there is no template to pick.
 *
 * Save as Draft needs only the name; Publish checks everything the API does
 * (contract A2) and needs no approval. A notification that is published is
 * no longer edited here: it can only be deactivated from its page.
 */
const TrainerNotificationForm = () => {
    const { notificationId } = useParams()
    const isEdit = Boolean(notificationId)
    const navigate = useNavigate()
    const [form, setForm] = useState(emptyTrainerNotificationForm)
    const [errors, setErrors] = useState({})
    const [submitting, setSubmitting] = useState('')
    const [submitError, setSubmitError] = useState('')
    const [options, setOptions] = useState(null)
    const [optionsError, setOptionsError] = useState('')
    const [totalTrainers, setTotalTrainers] = useState(null)
    // {state: 'idle' | 'loading' | 'ready' | 'error', count}
    const [reach, setReach] = useState({ state: 'idle', count: null })
    const [load, setLoad] = useState({
        state: isEdit ? 'loading' : 'ready',
        reason: '',
    })
    const [confirmLeave, setConfirmLeave] = useState(false)

    // The form as it was when it became ready, to tell whether Cancel loses anything.
    const snapshot = JSON.stringify(form)
    const baseline = useRef(null)
    useEffect(() => {
        if (load.state === 'ready' && baseline.current === null)
            baseline.current = snapshot
    }, [load.state, snapshot])
    const changed = baseline.current !== null && baseline.current !== snapshot

    useEffect(() => {
        TrainerNotificationService.audienceOptions()
            .then((res) => setOptions(res?.data ?? {}))
            .catch((err) =>
                setOptionsError(
                    `Grade groups, regions and products could not be loaded: ${errorMessage(
                        err?.response
                    )}`
                )
            )
        // The "All Trainers" card shows how many trainers that is.
        TrainerNotificationService.audienceCount({
            audience_type: AUDIENCE.ALL,
            audience_grades: [],
            audience_regions: [],
            audience_batch_types: [],
            audience_trainer_ids: [],
        })
            .then((res) =>
                setTotalTrainers(res?.data?.total_active_trainers ?? null)
            )
            .catch(() => setTotalTrainers(null))
    }, [])

    const loadDraft = () => {
        setLoad({ state: 'loading', reason: '' })
        TrainerNotificationService.get(notificationId)
            .then((res) => {
                const saved = res?.data?.notification
                if (!saved) {
                    setLoad({ state: 'error', reason: 'It was not found.' })
                    return
                }
                if (saved.status !== DRAFT) {
                    setLoad({
                        state: 'error',
                        reason: 'Only a draft can be edited. A published notification can be deactivated from its page.',
                    })
                    return
                }
                baseline.current = null
                setForm(formFromNotification(saved))
                setLoad({ state: 'ready', reason: '' })
            })
            .catch((err) =>
                setLoad({ state: 'error', reason: errorMessage(err?.response) })
            )
    }

    useEffect(() => {
        if (isEdit) loadDraft()
    }, [notificationId])

    // How many trainers the audience reaches, asked once it stops changing.
    // A later answer replaces an earlier one; an older one is dropped.
    const countBody = audienceCountBody(form, options)
    const countKey = JSON.stringify(countBody)
    const countRequest = useRef(0)
    useEffect(() => {
        if (!countBody) {
            countRequest.current += 1
            setReach({ state: 'idle', count: null })
            return undefined
        }
        const request = ++countRequest.current
        setReach((prev) => ({ ...prev, state: 'loading' }))
        const t = setTimeout(() => {
            TrainerNotificationService.audienceCount(countBody)
                .then((res) => {
                    if (request !== countRequest.current) return
                    setReach({ state: 'ready', count: res?.data?.count ?? 0 })
                    if (res?.data?.total_active_trainers)
                        setTotalTrainers(res.data.total_active_trainers)
                })
                .catch(() => {
                    if (request === countRequest.current)
                        setReach({ state: 'error', count: null })
                })
        }, COUNT_DELAY_MS)
        return () => clearTimeout(t)
    }, [countKey])

    /** Apply a change and drop the errors of the fields it touched. */
    const update = (patch) => {
        setForm((prev) => ({ ...prev, ...patch }))
        setSubmitError('')
        setErrors((prev) => {
            const keys = Object.keys(patch)
            if (keys.some((k) => AUDIENCE_FIELDS.includes(k)))
                keys.push('audience')
            if (!keys.some((k) => prev[k])) return prev
            const next = { ...prev }
            keys.forEach((k) => delete next[k])
            return next
        })
    }

    const leave = () =>
        navigate(
            isEdit
                ? `/trainer-notifications/${encodeURIComponent(notificationId)}`
                : '/trainer-notifications'
        )
    const onCancel = () => (changed ? setConfirmLeave(true) : leave())

    const submit = async (status) => {
        if (submitting) return
        const found = validateTrainerNotification(form, status)
        setErrors(found)
        if (Object.keys(found).length) {
            setSubmitError(
                status === PUBLISHED
                    ? 'Fix the highlighted fields to publish.'
                    : 'Enter a notification name to save the draft.'
            )
            return
        }
        setSubmitting(status)
        setSubmitError('')
        const body = trainerNotificationPayload(form, status)
        try {
            const res = isEdit
                ? await TrainerNotificationService.update(notificationId, body)
                : await TrainerNotificationService.create(body)
            const id = isEdit ? notificationId : res?.data?.notification_id
            baseline.current = snapshot
            navigate(
                id
                    ? `/trainer-notifications/${encodeURIComponent(id)}`
                    : '/trainer-notifications'
            )
        } catch (err) {
            // No answer: it may have been saved, and a second create would
            // make a duplicate, so the list is the place to check first.
            setSubmitError(
                isNoAnswer(err)
                    ? 'The server did not answer in time, so it may have been saved. Check the trainer notifications list before trying again.'
                    : `The notification was not saved: ${errorMessage(
                          err?.response
                      )}`
            )
        } finally {
            setSubmitting('')
        }
    }

    const summary =
        audienceTypeOf(form) === AUDIENCE.TRAINER_IDS
            ? [`${form.trainerIds.length} trainers listed`]
            : audienceSummary(formAudience(form), options)

    let reachText =
        'Choose who receives this to see how many trainers it reaches.'
    if (reach.state === 'loading') reachText = 'Counting trainers…'
    else if (reach.state === 'error')
        reachText = 'The number of trainers could not be counted.'
    else if (reach.state === 'ready')
        reachText = `~${reach.count.toLocaleString()} trainer${
            reach.count === 1 ? '' : 's'
        } will receive this`

    return (
        <Box sx={pageSx}>
            <Box
                sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 3, md: 4 }, pb: 0 }}
            >
                <NotificationHeader
                    title="Trainer Notifications"
                    subtitle={
                        isEdit
                            ? `Editing the draft${
                                  form.title ? `: ${form.title}` : ''
                              }`
                            : 'New trainer dashboard notification'
                    }
                    actions={
                        <Button variant="outlined" onClick={onCancel}>
                            Cancel
                        </Button>
                    }
                />

                {load.state === 'loading' && (
                    <Box
                        role="status"
                        sx={{ display: 'flex', justifyContent: 'center', p: 6 }}
                    >
                        <CircularProgress size={24} />
                    </Box>
                )}

                {load.state === 'error' && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 2,
                        }}
                    >
                        <ErrorBanner
                            message={`The draft could not be opened: ${load.reason}`}
                        />
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Button variant="outlined" onClick={loadDraft}>
                                Try again
                            </Button>
                            <Button variant="outlined" onClick={leave}>
                                Open its page
                            </Button>
                        </Box>
                    </Box>
                )}

                {load.state === 'ready' && (
                    <Paper elevation={0} sx={{ ...cardSx, p: 3 }}>
                        <Typography
                            component="h2"
                            sx={{
                                typography: 'h4',
                                fontWeight: 700,
                                color: TEXT.primary,
                            }}
                        >
                            Notification Details
                        </Typography>
                        <Typography
                            sx={{
                                typography: 'caption',
                                color: TEXT.muted,
                                mb: 3,
                            }}
                        >
                            Configure the notification that will appear on the
                            trainer dashboard.
                        </Typography>

                        {optionsError && (
                            <Box sx={{ mb: 2 }}>
                                <ErrorBanner message={optionsError} />
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'minmax(0, 1fr)',
                                    md: 'repeat(2, minmax(0, 1fr))',
                                },
                                columnGap: 3,
                                rowGap: 3,
                                alignItems: 'start',
                            }}
                        >
                            <TextField
                                required
                                size="small"
                                label="Notification Name"
                                placeholder="eg. Demo Process Update - May 2026"
                                value={form.title}
                                onChange={(e) =>
                                    update({ title: e.target.value })
                                }
                                inputProps={{ maxLength: MAX_TITLE }}
                                error={Boolean(errors.title)}
                                helperText={
                                    <Counter
                                        error={errors.title}
                                        length={form.title.length}
                                        max={MAX_TITLE}
                                    />
                                }
                            />

                            <SingleSelect
                                label="Category *"
                                options={CATEGORIES}
                                optionLabelText="label"
                                optionValueText="value"
                                value={form.category}
                                handleChange={(v) => update({ category: v })}
                            />
                            <Box>
                                <Typography
                                    id="tag-label"
                                    sx={{
                                        typography: 'caption',
                                        color: TEXT.secondary,
                                        mb: 1,
                                    }}
                                >
                                    Tag (visual treatment) *
                                </Typography>
                                <Box
                                    role="group"
                                    aria-labelledby="tag-label"
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                    }}
                                >
                                    {TAGS.map((t) => {
                                        const Icon = TAG_ICON[t.value]
                                        const tone = TAG[t.tone]
                                        const active = form.tag === t.value
                                        return (
                                            <Chip
                                                key={t.value}
                                                label={t.label}
                                                icon={
                                                    <Icon
                                                        sx={{
                                                            fontSize: ICON.md,
                                                        }}
                                                    />
                                                }
                                                variant="outlined"
                                                aria-pressed={active}
                                                onClick={() =>
                                                    update({ tag: t.value })
                                                }
                                                sx={{
                                                    fontWeight: 600,
                                                    borderColor: active
                                                        ? tone.fg
                                                        : SURFACE.border,
                                                    color: active
                                                        ? tone.fg
                                                        : TEXT.secondary,
                                                    background: active
                                                        ? tone.bg
                                                        : 'transparent',
                                                    '& .MuiChip-icon': {
                                                        color: tone.fg,
                                                    },
                                                }}
                                            />
                                        )
                                    })}
                                </Box>
                                {errors.tag && (
                                    <FormHelperText error>
                                        {errors.tag}
                                    </FormHelperText>
                                )}
                            </Box>

                            <Box sx={fullRow}>
                                <AudienceSegments
                                    form={form}
                                    onChange={update}
                                    options={options}
                                    totalTrainers={totalTrainers}
                                    errors={errors}
                                />
                            </Box>

                            <TextField
                                required
                                size="small"
                                type="datetime-local"
                                label="Start date/time (IST)"
                                value={form.start}
                                onChange={(e) =>
                                    update({ start: e.target.value })
                                }
                                InputLabelProps={{ shrink: true }}
                                error={Boolean(errors.start)}
                                helperText={
                                    errors.start ??
                                    'Auto-set to "now" - change to schedule'
                                }
                            />
                            <TextField
                                size="small"
                                type="datetime-local"
                                label="End date/expiry (IST, optional)"
                                value={form.end}
                                onChange={(e) =>
                                    update({ end: e.target.value })
                                }
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: form.start || undefined }}
                                error={Boolean(errors.end)}
                                helperText={
                                    errors.end ??
                                    (form.end
                                        ? 'Auto-removes from the bar after this'
                                        : 'No expiry - stays until deactivated')
                                }
                            />

                            <TextField
                                required
                                multiline
                                minRows={3}
                                label="Message"
                                value={form.message}
                                onChange={(e) =>
                                    update({ message: e.target.value })
                                }
                                inputProps={{ maxLength: MAX_MESSAGE }}
                                error={Boolean(errors.message)}
                                helperText={
                                    <Counter
                                        error={errors.message}
                                        length={form.message.length}
                                        max={MAX_MESSAGE}
                                    />
                                }
                            />
                            <StripPreview
                                tag={form.tag}
                                message={form.message}
                                ctaLabel={form.ctaLabel}
                            />

                            <TextField
                                required
                                size="small"
                                label="CTA Button Label"
                                placeholder="eg. View Policy"
                                value={form.ctaLabel}
                                onChange={(e) =>
                                    update({ ctaLabel: e.target.value })
                                }
                                error={Boolean(errors.ctaLabel)}
                                helperText={errors.ctaLabel}
                            />
                            <Box>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={form.ctaType}
                                    onChange={(_, type) =>
                                        type &&
                                        update({ ctaType: type, ctaLink: '' })
                                    }
                                    aria-label="Where the button goes"
                                    sx={{ mb: 1.5 }}
                                >
                                    <ToggleButton
                                        value={CTA_INTERNAL}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Dashboard page
                                    </ToggleButton>
                                    <ToggleButton
                                        value={CTA_EXTERNAL}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        External link
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                {form.ctaType === CTA_INTERNAL ? (
                                    <SingleSelect
                                        label="CTA Link *"
                                        options={INTERNAL_ROUTES}
                                        optionLabelText="label"
                                        optionValueText="value"
                                        value={form.ctaLink}
                                        handleChange={(v) =>
                                            update({ ctaLink: v })
                                        }
                                        error={Boolean(errors.ctaLink)}
                                        helperText={errors.ctaLink}
                                    />
                                ) : (
                                    <TextField
                                        required
                                        fullWidth
                                        size="small"
                                        label="CTA Link"
                                        placeholder="https://"
                                        value={form.ctaLink}
                                        onChange={(e) =>
                                            update({ ctaLink: e.target.value })
                                        }
                                        error={Boolean(errors.ctaLink)}
                                        helperText={
                                            errors.ctaLink ??
                                            'Opens in a new tab'
                                        }
                                    />
                                )}
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    minHeight: 40,
                                }}
                            >
                                <PillToggle
                                    checked={form.ackRequired}
                                    onChange={(v) => update({ ackRequired: v })}
                                    label="Requires acknowledgement"
                                />
                                <Box>
                                    <Typography
                                        sx={{
                                            typography: 'body2',
                                            color: TEXT.primary,
                                        }}
                                    >
                                        Requires acknowledgement
                                    </Typography>
                                    <Typography
                                        sx={{
                                            typography: 'caption',
                                            color: TEXT.muted,
                                        }}
                                    >
                                        Trainers open the link, then acknowledge
                                        it.
                                    </Typography>
                                </Box>
                            </Box>
                            <TextField
                                size="small"
                                label="Acknowledge button text"
                                value={form.ackLabel}
                                onChange={(e) =>
                                    update({ ackLabel: e.target.value })
                                }
                                disabled={!form.ackRequired}
                                required={form.ackRequired}
                                error={Boolean(errors.ackLabel)}
                                helperText={errors.ackLabel}
                            />

                            <Box
                                sx={{
                                    ...fullRow,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 2,
                                    borderRadius: `${RADIUS.md}px`,
                                    background: TAG.pinpoint.bg,
                                    border: `1px solid ${SURFACE.border}`,
                                }}
                                role="status"
                                aria-live="polite"
                            >
                                <PeopleAltOutlinedIcon
                                    sx={{ color: TAG.pinpoint.fg }}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            typography: 'h6',
                                            fontWeight: 700,
                                            color: TEXT.primary,
                                        }}
                                    >
                                        {reachText}
                                    </Typography>
                                    {summary.length > 0 && (
                                        <Typography
                                            sx={{
                                                typography: 'caption',
                                                color: TEXT.secondary,
                                            }}
                                        >
                                            {summary.join(' · ')}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                )}
            </Box>

            {load.state === 'ready' && (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 2,
                        mt: 3,
                        background: SURFACE.card,
                        borderTop: `1px solid ${SURFACE.border}`,
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 1400,
                            mx: 'auto',
                            px: { xs: 3, md: 4 },
                            py: 1.5,
                        }}
                    >
                        {submitError && (
                            <Box sx={{ mb: 1.5 }}>
                                <ErrorBanner message={submitError} />
                            </Box>
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography
                                sx={{
                                    typography: 'caption',
                                    color: TEXT.muted,
                                }}
                            >
                                Note: Trainer dashboard notifications publish
                                without approval.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => submit(DRAFT)}
                                    disabled={Boolean(submitting)}
                                    startIcon={
                                        submitting === DRAFT ? (
                                            <CircularProgress size={14} />
                                        ) : null
                                    }
                                >
                                    {submitting === DRAFT
                                        ? 'Saving…'
                                        : 'Save as Draft'}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => submit(PUBLISHED)}
                                    disabled={Boolean(submitting)}
                                    startIcon={
                                        submitting === PUBLISHED ? (
                                            <CircularProgress size={14} />
                                        ) : null
                                    }
                                >
                                    {submitting === PUBLISHED
                                        ? 'Publishing…'
                                        : 'Publish Notification'}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}

            <ConfirmDialog
                open={confirmLeave}
                title="Leave this page?"
                message="What you entered here is not saved and is lost if you leave."
                confirmLabel="Leave without saving"
                destructive
                onConfirm={() => {
                    setConfirmLeave(false)
                    leave()
                }}
                onCancel={() => setConfirmLeave(false)}
            />
        </Box>
    )
}

export default TrainerNotificationForm
